import { useCallback, useEffect, useRef, useState } from 'react';
import { ViewerControl } from '@prizmdoc/viewer-core';
import { getScrollContainer } from '../utils';
import { SCROLL_MARK_TYPE, PRESENT_COLOR, PAGENUMBER_MARK_TYPE } from '../constants';
import { MouseToolType } from '../../types';

export const usePauseDocument = (
  viewerControl: typeof ViewerControl | null,
  isTheatreMode: boolean,
  initializingRef: React.MutableRefObject<number | null>,
  selectedMouseTool: any,
  hardDisconnectOnlyHearingRoom: boolean,
) => {
  const [isDisconnected, setIsDisconnected] = useState<boolean>(false);
  const [pauseTheatreMode, setPauseTheatreMode] = useState(false);

  const pauseTheatreModeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const scrollContainer = getScrollContainer(viewerControl);

  const disconnectPauseTheatreModeHandler = () => {
    if (hardDisconnectOnlyHearingRoom) setPauseTheatreMode((prev) => (!prev ? true : prev));
    clearPauseTheatreModeTimeout();
    setIsDisconnected(true);
  };

  const unpauseTheatreMode = useCallback(() => {
    setPauseTheatreMode((prev) => (prev ? false : prev));
    if (isDisconnected) setIsDisconnected(false);
  }, [isDisconnected]);

  const clearPauseTheatreModeTimeout = useCallback(() => {
    if (pauseTheatreModeTimeoutRef.current) clearTimeout(pauseTheatreModeTimeoutRef.current);
  }, []);

  const pauseTheatreModeHandler = useCallback(() => {
    if (
      isTheatreMode &&
      !hardDisconnectOnlyHearingRoom &&
      !isDisconnected &&
      !initializingRef.current
    ) {
      setPauseTheatreMode((prev) => (!prev ? true : prev));
      clearPauseTheatreModeTimeout();
      pauseTheatreModeTimeoutRef.current = setTimeout(() => {
        unpauseTheatreMode();
      }, 3000);
    }
  }, [
    initializingRef,
    isTheatreMode,
    isDisconnected,
    clearPauseTheatreModeTimeout,
    unpauseTheatreMode,
    hardDisconnectOnlyHearingRoom,
  ]);

  const unpauseTheatreModeHandler = () => {
    setPauseTheatreMode((prev) => (prev ? false : prev));
    if (isDisconnected) setIsDisconnected(false);
  };

  // MOUNT
  useEffect(() => {
    return () => {
      if (pauseTheatreModeTimeoutRef.current) clearTimeout(pauseTheatreModeTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!viewerControl) return;

    const mouseUpDownHandler = () => {
      if (selectedMouseTool !== MouseToolType.PanAndEdit) {
        pauseTheatreModeHandler();
      }
    };

    const markCreatedHandler = ({ mark }: any) => {
      const isScrollMark = mark.type === SCROLL_MARK_TYPE;
      const isPageNumberMark = mark.type === PAGENUMBER_MARK_TYPE;
      const isPresentMark = mark.fillColor === PRESENT_COLOR;
      // if it's not scroll mark and not presentationmark, we need to pause (user is creating mark)
      if (isTheatreMode && !isScrollMark && !isPresentMark && !isPageNumberMark) {
        pauseTheatreModeHandler();
      }
    };

    const documentRotatedHandler = () => {
      pauseTheatreModeHandler();
    };

    const documentScrolledHandler = () => {
      pauseTheatreModeHandler();
    };

    const documentPageChangedHandler = () => {
      pauseTheatreModeHandler();
    };

    const documentScaleChangedHandler = () => {
      pauseTheatreModeHandler();
    };

    if (isTheatreMode) {
      // events that bring viewer in pause mode
      viewerControl?.on('DocumentRotated', documentRotatedHandler);
      scrollContainer?.addEventListener('scroll', documentScrolledHandler);
      scrollContainer?.addEventListener('mouseup', mouseUpDownHandler);
      scrollContainer?.addEventListener('mousedown', mouseUpDownHandler);
      viewerControl?.on('MarkCreated', markCreatedHandler);
      viewerControl?.on('PageChanged', documentPageChangedHandler);
      viewerControl?.on('ScaleChanged', documentScaleChangedHandler);
    } else {
      // TO-DO
      // when page or scale changes we need to update customToolbox
      // viewerControl.on('PageChanged', invalidate);
      // viewerControl.on('ScaleChanged', invalidate);
    }

    const vr = viewerControl;

    return () => {
      if (isTheatreMode) {
        vr?.off('DocumentRotated', documentRotatedHandler);
        scrollContainer?.removeEventListener('scroll', documentScrolledHandler);
        scrollContainer?.removeEventListener('mouseup', mouseUpDownHandler);
        scrollContainer?.removeEventListener('mousedown', mouseUpDownHandler);
        vr?.off('MarkCreated', markCreatedHandler);
        vr?.off('PageChanged', documentPageChangedHandler);
        vr?.off('ScaleChanged', documentScaleChangedHandler);
      } else {
        // viewerControl.off('PageChanged', invalidate);
        // viewerControl.off('ScaleChanged', invalidate);
      }
    };
  }, [isTheatreMode, pauseTheatreModeHandler, scrollContainer, selectedMouseTool, viewerControl]);

  return {
    unpauseTheatreModeHandler,
    disconnectPauseTheatreModeHandler,
    pauseTheatreMode,
    isDisconnected,
  };
};
